import BN from "bignumber.js";
import * as web3 from "@solana/web3.js";
import ApiRequest from "@/common/lib/apiRequest";
import StakeApiRequest from "@/common/lib/stakeApiRequest";
import formatDate from "@/filters/dateTime.filter";
import formatNumber from "@/filters/numberFormat.filter";
import currencyFormat from "@/filters/currency.filter";

const mutationTypes = {
  SET_VAULTS_AUM: "portfolio/SET_VAULTS_AUM",
  SET_TENDERMINT_AUM: "portfolio/SET_TENDERMINT_AUM",
  SET_ETHEREUM_AUM: "portfolio/SET_ETHEREUM_AUM",
  SET_SOLANA_AUM: "portfolio/SET_SOLANA_AUM",
  SET_SUI_AUM: "portfolio/SET_SUI_AUM",
  SET_COINS_AVAILABLE: "portfolio/SET_COINS_AVAILABLE",
  SET_HISTORICAL_BALANCES: "portfolio/SET_HISTORICAL_BALANCES",
  SET_UNBONDINGS: "portfolio/SET_UNBONDINGS",
};

const createState = () => ({
  vaults: {},
  ethereums: {},
  tendermints: {},
  solanas: {},
  suis: {},
  coinsAvailable: {},
  historicalBalances: {},
  unbondings: {},
});

const state = createState();

const actions = {
  async fetchAUM({ commit, ...rest }) {
    const walletSelected = rest.rootGetters["userWallets/walletSelected"];
    let result = await ApiRequest.get("/account/total_aum");
    if (walletSelected && walletSelected.address) {
      result = result.filter(
        item =>
          item.address.toLowerCase() === walletSelected.address.toLowerCase()
      );
    }
    const ethAddresses = result.filter(item => item.network === "ethereum");
    const tendermintAddresses = result.filter(
      item =>
        !!item.tendermintStaking &&
        rest.rootGetters["projectConfig/projectByNetworkMap"][item.network]
          ?.implemented
    );
    const solanaAddresses = result.filter(item => item.network === "solana");
    const suiAddresses = result.filter(item => item.network === "sui");
    setVaultsAUM({ commit, ...rest }, ethAddresses);
    setEthereumAUM({ commit, ...rest }, ethAddresses);
    setTendermintAUM({ commit, ...rest }, tendermintAddresses);
    setSolanaAUM({ commit, ...rest }, solanaAddresses);
    setSuiAUM({ commit, ...rest }, suiAddresses);
    await Promise.all([
      // setCoinsAvailable(
      //   { commit, ...rest },
      //   { ethAddresses, tendermintAddresses, solanaAddresses }
      // ),
      setUnbondings({ commit, ...rest }, tendermintAddresses),
    ]);
  },
  async fetchRawBalanceHistory({ rootGetters }, { vault, time }) {
    const coins = rootGetters["projectConfig/vaultGroups"].find(
      vg => vg.name === vault
    ).tokens;
    const wallets = rootGetters["userWallets/walletList"].result || [];
    const walletSelected = rootGetters["userWallets/walletSelected"];
    const ethAddresses = wallets.filter(
      wallet =>
        wallet.coin_network === "ethereum" &&
        (!walletSelected.address || walletSelected.address === wallet.address)
    );
    const promises = [];
    ethAddresses.forEach(({ address }) => {
      promises.push(
        Promise.all(
          coins.map(async coin => {
            const rawBalances = await ApiRequest.get(
              `sc/stats/balance/${vault}/${coin.coin}/${address}`,
              {
                params: {
                  time,
                },
              }
            );
            const data = [];
            rawBalances
              .sort((a, b) => a.createdAt - b.createdAt)
              .forEach(item => {
                if (
                  item.expectedReturn !== null &&
                  item.expectedReturn !== undefined
                ) {
                  const [year, month, day] = item.date.split("-");
                  item.originalCreatedAt = item.createdAt;
                  item.createdAt = new Date(
                    year,
                    Number(month - 1),
                    day
                  ).valueOf();
                  // purpose: to add missed days to the array
                  if (data.length) {
                    const durations =
                      item.createdAt - data[data.length - 1].createdAt;
                    if (durations > 86400000) {
                      const missedDays = durations / 86400000 - 1;
                      const currentExpectedReturn =
                        data[data.length - 1].expectedReturn;
                      const difPerDay = new BN(item.expectedReturn)
                        .minus(currentExpectedReturn)
                        .dividedBy(missedDays + 1);
                      Array(missedDays)
                        .fill(0)
                        .forEach((value, index) => {
                          data.push({
                            expectedReturn: new BN(currentExpectedReturn)
                              .plus(difPerDay.multipliedBy(index + 1))
                              .toNumber(),
                            createdAt:
                              data[data.length - 1].createdAt +
                              (index + 1) * 86400000,
                            originalCreatedAt:
                              data[data.length - 1].originalCreatedAt +
                              (index + 1) * 86400000,
                          });
                        });
                    }
                  }
                  data.push({
                    createdAt: item.createdAt,
                    originalCreatedAt: item.originalCreatedAt,
                    expectedReturn: item.expectedReturn,
                  });
                }
              });
            return {
              [coin.coin + "_" + vault]: data,
            };
          })
        )
      );
    });
    return Promise.all(promises);
  },
  async fetchHistoricalBalances({ rootGetters, dispatch }, { vault, time }) {
    const getTokenPriceInUsd = rootGetters["appConfig/getTokenPriceInUsd"];
    const values = await dispatch("fetchRawBalanceHistory", { vault, time });
    const result = { combined: {} };
    values.forEach(item => {
      const reducedItem = item.reduce((acc, cur) => ({ ...acc, ...cur }), {});
      for (let itemName in reducedItem) {
        const coinName = itemName.split("_")[0];
        if (!result[itemName]) {
          result[itemName] = {};
        }
        reducedItem[itemName].forEach(({ createdAt, expectedReturn }) => {
          const currentValue = new BN(expectedReturn).multipliedBy(
            getTokenPriceInUsd(coinName)
          );
          result[itemName][createdAt] = new BN(result[itemName][createdAt] || 0)
            .plus(currentValue)
            .toNumber();
          result.combined[createdAt] = new BN(result.combined[createdAt] || 0)
            .plus(currentValue)
            .toNumber();
        });
      }
    });
    for (const key in result) {
      result[key] = timeObjectToArray(result[key]);
    }
    return result;
  },
  async getCsvDataForBalanceHistory({ rootGetters, dispatch }) {
    const vaultGroups = rootGetters["projectConfig/vaultGroups"].map(
      item => item.name
    );
    const tokens = rootGetters["projectConfig/vaultGroups"]
      .map(vg => vg.tokens.map(token => ({ ...token, vault: vg.name })))
      .flat();
    const getTokenPriceInUsd = rootGetters["appConfig/getTokenPriceInUsd"];
    const values = await Promise.all(
      vaultGroups.map(async vault =>
        dispatch("fetchRawBalanceHistory", { vault })
      )
    );
    const valuesByDate = {};
    values.forEach(valueItems => {
      valueItems.forEach(item => {
        const reducedItem = item.reduce((acc, cur) => ({ ...acc, ...cur }), {});
        for (let itemName in reducedItem) {
          const coinName = itemName.split("_")[0];
          reducedItem[itemName].forEach(
            ({ createdAt, originalCreatedAt, expectedReturn }) => {
              const previousCreatedAt = createdAt - 86400000;
              const currentValue = new BN(expectedReturn)
                .multipliedBy(getTokenPriceInUsd(coinName))
                .toNumber();
              if (!valuesByDate[createdAt]) {
                valuesByDate[createdAt] = {
                  originalCreatedAt,
                  combined: {
                    expectedReturnDollar: 0,
                    balanceChangeDollar: 0,
                  },
                };
              }
              if (!valuesByDate[createdAt][itemName]) {
                valuesByDate[createdAt][itemName] = {
                  expectedReturn: 0,
                  expectedReturnDollar: 0,
                  balanceChange: 0,
                  balanceChangeDollar: 0,
                };
              }
              valuesByDate[createdAt][itemName].expectedReturn = new BN(
                valuesByDate[createdAt][itemName].expectedReturn
              )
                .plus(expectedReturn)
                .toNumber();
              valuesByDate[createdAt][itemName].expectedReturnDollar = new BN(
                valuesByDate[createdAt][itemName].expectedReturnDollar
              )
                .plus(currentValue)
                .toNumber();

              valuesByDate[createdAt].combined.expectedReturnDollar = new BN(
                valuesByDate[createdAt].combined.expectedReturnDollar
              )
                .plus(currentValue)
                .toNumber();

              if (valuesByDate[previousCreatedAt]?.[itemName]) {
                valuesByDate[createdAt][itemName].balanceChange = new BN(
                  valuesByDate[createdAt][itemName].expectedReturn
                )
                  .minus(
                    valuesByDate[previousCreatedAt][itemName].expectedReturn
                  )
                  .toNumber();

                valuesByDate[createdAt][itemName].balanceChangeDollar = new BN(
                  valuesByDate[createdAt][itemName].expectedReturnDollar
                )
                  .minus(
                    valuesByDate[previousCreatedAt][itemName]
                      .expectedReturnDollar
                  )
                  .toNumber();

                valuesByDate[createdAt].combined.balanceChangeDollar = new BN(
                  valuesByDate[createdAt].combined.expectedReturnDollar
                )
                  .minus(
                    valuesByDate[previousCreatedAt].combined
                      .expectedReturnDollar
                  )
                  .toNumber();
              }
            }
          );
        }
      });
    });
    let result = "Date";
    const tokenKeys = tokens.map(token => {
      return {
        name: token.identity,
        nameWithVault: token.coin + "_" + token.vault,
      };
    });
    tokenKeys.forEach(({ name }) => {
      result += `,${name} Balance,${name} 24H Balance Change`;
    });
    result += ",Total Balance,24H Total Balance Change";
    for (const date in valuesByDate) {
      const _values = valuesByDate[date];
      result +=
        "\n" +
        formatDate(Number(_values.originalCreatedAt), "yyyy-MM-dd hh:mm a zzz");
      tokenKeys.forEach(({ nameWithVault }) => {
        if (_values[nameWithVault]) {
          result += `,"${formatNumber(
            _values[nameWithVault].expectedReturn,
            "0,0.000000"
          )} ($${currencyFormat(
            _values[nameWithVault].expectedReturnDollar
          )})"`;
          result += `,"${formatNumber(
            _values[nameWithVault].balanceChange,
            "0,0.000000"
          )} ($${currencyFormat(
            Math.abs(_values[nameWithVault].balanceChangeDollar)
          )})"`;
        } else {
          result += ",,";
        }
      });
      result += `,"$${currencyFormat(
        _values.combined.expectedReturnDollar
      )}","$${currencyFormat(Math.abs(_values.combined.balanceChangeDollar))}"`;
    }
    return result;
  },
  async fetchRawStakeBalanceHistory({ rootGetters }, { time }) {
    const getTokenPriceInUsd = rootGetters["appConfig/getTokenPriceInUsd"];
    const getProjectByName = rootGetters["projectConfig/getProjectByName"];
    const walletSelected = rootGetters["userWallets/walletSelected"];
    let wallets = rootGetters["userWallets/walletList"].result || [];
    if (walletSelected.address) {
      wallets = wallets.filter(
        wallet => wallet.address === walletSelected.address
      );
    }
    const promises = wallets
      .filter(wallet => !['tron', 'ronin'].includes(wallet.coin_network))
      .map(wallet =>
        wallet.coin_network === "ethereum"
          ? getStakeEthBasedHistory(wallet, time)
        : wallet.coin_network === "solana" || wallet.coin_network === "sui"
        ? getStakeNoethBasedBalanceHistory(wallet, time)
        : getStakeTendermintBasedBalanceHistory(wallet, time)
    );
    const stakeBalances = await Promise.all(promises);
    const accumulationBalances = {
      combined: {},
    };
    stakeBalances.forEach(item => {
      for (const projectName in item) {
        const itemData = item[projectName];
        if (!accumulationBalances[projectName]) {
          accumulationBalances[projectName] = {};
        }
        itemData.forEach(({ balance, reward, date }) => {
          const [year, month, day] = date.split("-");
          date = new Date(year, Number(month - 1), day).valueOf();
          if (!accumulationBalances[projectName][date]) {
            accumulationBalances[projectName][date] = {
              balance: 0,
              value: 0,
            };
          }
          if (!accumulationBalances.combined[date]) {
            accumulationBalances.combined[date] = {
              balance: 0,
              value: 0,
            };
          }
          accumulationBalances[projectName][date].balance = new BN(
            accumulationBalances[projectName][date].balance
          )
            .plus(balance || 0)
            .plus(reward || 0)
            .toNumber();
          accumulationBalances[projectName][date].value = new BN(
            accumulationBalances[projectName][date].balance
          )
            .multipliedBy(
              getTokenPriceInUsd(getProjectByName(projectName).staking.token)
            )
            .toNumber();
          accumulationBalances.combined[date].value = new BN(
            accumulationBalances.combined[date].value
          )
            .plus(accumulationBalances[projectName][date].value)
            .toNumber();
        });
      }
    });
    return accumulationBalances;
  },
  async fetchStakeHistoricalBalances({ dispatch }, { time }) {
    const result = await dispatch("fetchRawStakeBalanceHistory", {
      time,
    });
    for (const key in result) {
      result[key] = timeObjectToArray(
        result[key],
        (obj, key) => obj[key].value
      );
    }
    return result;
  },
  async getCsvDataForStakeBalanceHistory({ dispatch, rootGetters }) {
    const getProjectByName = rootGetters["projectConfig/getProjectByName"];
    const rawStakeBalances = await dispatch("fetchRawStakeBalanceHistory", {});
    const allDates = Object.keys(rawStakeBalances.combined).sort();
    const projectNames = [];
    let output = "Date";
    Object.keys(rawStakeBalances).forEach(projectName => {
      if (projectName !== "combined") {
        projectNames.push(projectName);
        const tokenName = getProjectByName(
          projectName
        )?.staking.token.toUpperCase();
        if (!tokenName) return;
        output += `,${tokenName}, ${tokenName} 24H Change`;
      }
    });
    projectNames.push("combined");
    output += ",Total AUM,24H Total AUM Change";
    allDates.forEach(date => {
      output += "\n" + formatDate(Number(date), "yyyy-MM-dd hh:mm a zzz");
      projectNames.forEach(projectName => {
        output += `,"$${currencyFormat(
          rawStakeBalances[projectName][date]?.value ?? 0
        )}"`;
        const change24h =
          (rawStakeBalances[projectName][date]?.value ?? 0) -
          (rawStakeBalances[projectName][date - 86400000]?.value ?? 0);
        output += `,"${change24h < 0 ? "‒" : ""}$${currencyFormat(
          Math.abs(change24h)
        )}"`;
      });
    });
    return output;
  },
  async fetchAllHistoricalBalances(
    { commit, rootGetters, dispatch },
    { time }
  ) {
    try {
      const vaults = rootGetters["projectConfig/vaultGroups"].map(
        item => item.name
      );
      const [vaultsData, stakeData] = await Promise.all([
        Promise.all(
          vaults.map(vault =>
            dispatch("fetchHistoricalBalances", { vault, time }).then(res =>
              timeArrayToObject(res.combined)
            )
          )
        ),
        dispatch("fetchStakeHistoricalBalances", { time }).then(
          res => res.combined
        ),
      ]);
      const vaultsResult = combineTimeData(vaultsData);
      const all = combineTimeData([vaultsResult, timeArrayToObject(stakeData)]);
      const result = {
        stake: stakeData,
        vaults: timeObjectToArray(vaultsResult),
        all: timeObjectToArray(all),
      };
      commit(mutationTypes.SET_HISTORICAL_BALANCES, result);
      return result;
    } catch (error) {
      return {
        stake: [],
        vaults: [],
        all: [],
      };
    }
  },
};

//#region helpers
const getTokenBalanceUrl = (name, address) => {
  if (name === "eth") return `/sc/eth_balance/${address}`;
  return `/sc/erc20_balance/${name}/${address}`;
};

const fetchVaultCoinBalance = async (addresses, coins) => {
  const promises = [];
  addresses.forEach(addr => {
    coins.forEach(coin => {
      promises.push(ApiRequest.get(getTokenBalanceUrl(coin, addr.address)));
    });
  });
  const data = await Promise.all(promises);
  const result = data.reduce((acc, cur) => {
    const tokenLower = cur.token.toLowerCase();
    acc[tokenLower] = acc[tokenLower] || {
      token: tokenLower,
      balance: 0,
    };
    acc[tokenLower].balance = new BN(acc[tokenLower].balance)
      .plus(cur.value)
      .toNumber();
    return acc;
  }, {});
  return result;
};

const fetchKyberAvailable = async addresses => {
  const data = await Promise.all(
    addresses.map(async ({ address }) =>
      ApiRequest.get("kyberdao/staker/available_balance/" + address)
    )
  );
  const result = {
    knc: {
      token: "knc",
      balance: data.reduce(
        (acc, cur) => new BN(acc).plus(cur.value).toNumber(),
        0
      ),
    },
  };
  return result;
};

const fetchSkaleAvailable = async addresses => {
  const data = await Promise.all(
    addresses.map(async ({ address }) =>
      ApiRequest.get("/eth/skale/delegator/available_balance/" + address)
    )
  );
  const result = {
    skl: {
      token: "skl",
      balance: data.reduce(
        (acc, cur) =>
          new BN(acc)
            .plus(cur.balance)
            .minus(cur.locked)
            .toNumber(),
        0
      ),
    },
  };
  return result;
};

const fetchTheGraphAvailable = async addresses => {
  const data = await Promise.all(
    addresses.map(async ({ address }) =>
      ApiRequest.get("/eth/thegraph/delegator/available_balance/" + address)
    )
  );
  const result = {
    grt: {
      token: "grt",
      balance: data.reduce(
        (acc, cur) => new BN(acc).plus(cur.balance).toNumber(),
        0
      ),
    },
  };
  return result;
};

const fetchCelerAvailable = async addresses => {
  const data = await Promise.all(
    addresses.map(async ({ address }) =>
      ApiRequest.get("/eth/celer/delegator/available_balance/" + address)
    )
  );
  const result = {
    celr: {
      token: "celr",
      balance: data.reduce(
        (acc, cur) => new BN(acc).plus(cur.balance).toNumber(),
        0
      ),
    },
  };
  return result;
};

const fetchSolanaAvailable = async addresses => {
  const connection = new web3.Connection(process.env.VUE_APP_SOLANA_ENDPOINT);
  const data = await Promise.all(
    addresses.map(async ({ address }) =>
      connection.getBalance(new web3.PublicKey(address))
    )
  );
  const result = {
    sol: {
      token: "sol",
      balance: data.reduce(
        (acc, cur) => new BN(acc).plus(cur / web3.LAMPORTS_PER_SOL).toNumber(),
        0
      ),
    },
  };
  return result;
};

const fetchTendermintAvailable = async (addresses, projectByNetworkMap) => {
  const result = {};
  const promises = [];
  addresses.forEach(addr => {
    const project = projectByNetworkMap[addr.network];
    if (project?.status.active && project.staking.chainId) {
      promises.push(
        (async () => {
          try {
            const ret = await StakeApiRequest.get(
              `/balance/${project.name}/${project.staking.chainId}/${addr.address}`
            );
            return ret;
          } catch (error) {
            return {
              network: addr.network,
              balances: [],
            };
          }
        })()
      );
    }
  });
  const data = await Promise.all(promises);
  data.forEach(item => {
    const project = projectByNetworkMap[item.network];
    const { value } = item.balances.find(
      bl => bl.denom === project.staking.denom
    ) || { value: 0 };
    result[project.staking.token] = result[project.staking.token] || {
      balance: 0,
      token: project.staking.token,
    };
    result[project.staking.token].balance = new BN(
      result[project.staking.token].balance
    )
      .plus(new BN(value).dividedBy(project.staking.conversionRate))
      .toNumber();
  });
  return result;
};

const setCoinsAvailable = async (
  { commit, rootGetters },
  { ethAddresses, tendermintAddresses, solanaAddresses }
) => {
  const data = await Promise.all([
    fetchVaultCoinBalance(
      ethAddresses,
      rootGetters["projectConfig/vaultCoinNames"]
    ),
    fetchKyberAvailable(ethAddresses),
    fetchSkaleAvailable(ethAddresses),
    fetchTheGraphAvailable(ethAddresses),
    fetchCelerAvailable(ethAddresses),
    fetchTendermintAvailable(
      tendermintAddresses,
      rootGetters["projectConfig/projectByNetworkMap"]
    ),
    fetchSolanaAvailable(solanaAddresses),
  ]);
  const result = data.reduce(
    (acc, cur) => ({
      ...acc,
      ...cur,
    }),
    {}
  );
  commit(mutationTypes.SET_COINS_AVAILABLE, result);
};

const setUnbondings = async ({ commit, rootGetters }, addresses) => {
  const projectByNetworkMap = rootGetters["projectConfig/projectByNetworkMap"];
  const data = await Promise.all(
    addresses.map(async item => {
      return getStakeTendermintUnbondings(
        item.address,
        projectByNetworkMap[item.network]
      );
    })
  );
  const result = data.reduce(
    (acc, cur) => ({
      ...acc,
      ...cur,
    }),
    {}
  );
  commit(mutationTypes.SET_UNBONDINGS, result);
};

const setVaultsAUM = ({ commit }, addresses) => {
  const result = {};
  addresses.forEach(addr => {
    addr.uvaultStaking.forEach(us => {
      const tokenLower = us.token.toLowerCase();
      if (!result[us.vaultType]) {
        result[us.vaultType] = {};
      }
      if (!result[us.vaultType][tokenLower]) {
        result[us.vaultType][tokenLower] = {
          shares: 0,
          expectedReturn: 0,
          lastExpectedReturn: 0,
          balanceBefore: 0,
          balanceAfter: 0,
          token: tokenLower,
          balanceChange: 0,
          balanceChangeInPercent: 0,
        };
      }
      result[us.vaultType][tokenLower].shares = new BN(
        result[us.vaultType][tokenLower].shares
      )
        .plus(us.shares)
        .toNumber();
      result[us.vaultType][tokenLower].expectedReturn = new BN(
        result[us.vaultType][tokenLower].expectedReturn
      )
        .plus(us.expectedReturn)
        .toNumber();
      result[us.vaultType][tokenLower].balanceBefore = new BN(
        result[us.vaultType][tokenLower].balanceBefore
      )
        .plus(us.balanceBefore || 0)
        .toNumber();
      result[us.vaultType][tokenLower].balanceAfter = new BN(
        result[us.vaultType][tokenLower].balanceAfter
      )
        .plus(us.balanceAfter || 0)
        .toNumber();
      result[us.vaultType][tokenLower].balanceChange = new BN(
        result[us.vaultType][tokenLower].balanceAfter
      )
        .minus(result[us.vaultType][tokenLower].balanceBefore || 0)
        .toNumber();
      result[us.vaultType][tokenLower].balanceChangeInPercent =
        new BN(result[us.vaultType][tokenLower].balanceChange)
          .dividedBy(
            result[us.vaultType][tokenLower].balanceBefore ||
              result[us.vaultType][tokenLower].balanceChange
          )
          .multipliedBy(100)
          .toNumber() || 0;
    });
  });
  commit(mutationTypes.SET_VAULTS_AUM, result);
};

const setTendermintAUM = ({ commit, rootGetters }, addresses) => {
  const projectByNetworkMap = rootGetters["projectConfig/projectByNetworkMap"];
  const result = {};
  addresses.forEach(addr => {
    const project = projectByNetworkMap[addr.network];
    if (project) {
      if (!result[addr.network]) {
        result[addr.network] = {
          staked: {
            balance: 0,
            token: project.staking.token,
          },
          rewards: {
            balance: 0,
            token: project.staking.token,
          },
        };
      }
      if (addr.tendermintStaking.delegation) {
        result[addr.network].staked.balance = new BN(
          result[addr.network].staked.balance
        )
          .plus(
            new BN(addr.tendermintStaking.delegation.balance).dividedBy(
              project.staking.conversionRate
            )
          )
          .toNumber();
      }
      if (addr.tendermintStaking.rewards.length) {
        const totalRewards = addr.tendermintStaking.rewards
          .filter(item => item?.denom === project.staking.denom)
          .reduce((sum, { value }) => new BN(sum).plus(value).toNumber(), 0);
        result[addr.network].rewards.balance = new BN(
          result[addr.network].rewards.balance
        )
          .plus(new BN(totalRewards).dividedBy(project.staking.conversionRate))
          .toNumber();
      }
    }
  });
  commit(mutationTypes.SET_TENDERMINT_AUM, result);
};

const setEthereumAUM = ({ commit }, addresses) => {
  const result = {
    kyber: {
      staked: {
        balance: 0,
        token: "knc",
      },
      rewards: {
        balance: 0,
        token: "knc",
      },
    },
    skale: {
      staked: {
        locked: 0,
        balance: 0,
        token: "skl",
      },
      rewards: {
        balance: 0,
        token: "skl",
      },
    },
    thegraph: {
      staked: {
        balance: 0,
        token: "grt",
      },
      rewards: {
        balance: 0,
        token: "grt",
      },
    },
    celer: {
      staked: {
        balance: 0,
        token: "celr",
      },
      rewards: {
        balance: 0,
        token: "celr",
      },
    },
  };
  addresses.forEach(addr => {
    if (addr.kyberDAOStaking) {
      result.kyber.staked.balance = new BN(result.kyber.staked.balance)
        .plus(addr.kyberDAOStaking.value)
        .toNumber();
    }
    if (addr.skaleStaking) {
      result.skale.staked.balance = new BN(result.skale.staked.balance)
        .plus(addr.skaleStaking.value)
        .toNumber();
      result.skale.rewards.balance = new BN(result.skale.rewards.balance)
        .plus(addr.skaleStaking.reward?.value ?? 0)
        .toNumber();
    }
    if (addr.thegraphStaking) {
      result.thegraph.staked.balance = new BN(result.thegraph.staked.balance)
        .plus(addr.thegraphStaking.value)
        .toNumber();
    }
    if (addr.celerStaking) {
      result.celer.staked.balance = new BN(result.celer.staked.balance)
        .plus(addr.celerStaking.value)
        .toNumber();
      result.celer.rewards.balance = new BN(result.celer.rewards.balance)
        .plus(addr.celerStaking.reward?.value ?? 0)
        .toNumber();
    }
  });
  result.skale.staked.locked = result.skale.staked.balance;
  commit(mutationTypes.SET_ETHEREUM_AUM, result);
};

const setSolanaAUM = ({ commit }, addresses) => {
  const result = {
    solana: {
      staked: {
        balance: 0,
        token: "sol",
      },
      rewards: {
        balance: 0,
        token: "sol",
      },
    },
  };
  addresses.forEach(addr => {
    if (addr.solanaStaking) {
      result.solana.staked.balance = new BN(result.solana.staked.balance)
        .plus(addr.solanaStaking.value)
        .toNumber();
    }
  });
  commit(mutationTypes.SET_SOLANA_AUM, result);
};

const setSuiAUM = ({ commit }, addresses) => {
  const result = {
    sui: {
      staked: {
        balance: 0,
        token: "sui",
      },
      rewards: {
        balance: 0,
        token: "sui",
      },
    },
  };

  addresses.forEach(addr => {
    if (addr.suiStaking) {
      result.sui.staked.balance = new BN(result.sui.staked.balance)
        .plus(addr.suiStaking.value)
        .toNumber();
      result.sui.rewards.balance = new BN(result.sui.rewards.balance)
        .plus(Number(addr.suiStaking.reward?.value) || 0)
        .toNumber();
    }
  });
  commit(mutationTypes.SET_SUI_AUM, result);
};
const getStakeEthBasedHistory = async (wallet, time) => {
  const requests = [
    `/kyberdao/stats/balance/${wallet.address}`,
    `/eth/skale/stats/balance/${wallet.address}`,
    `/eth/celer/stats/balance/${wallet.address}`,
  ];
  const [kyber, skale, celer] = await Promise.all(
    requests.map(r => ApiRequest.get(r, { params: { time } }))
  );
  return {
    kyber,
    thegraph: [],
    skale,
    celer,
  };
};

const getStakeTendermintBasedBalanceHistory = async (wallet, time) => {
  const network = wallet.coin_network;
  const data = await StakeApiRequest.get(
    `/stats/balance/${network}/${wallet.address}`,
    {
      params: {
        time,
      },
    }
  );
  return {
    [network]: data,
  };
};

const getStakeNoethBasedBalanceHistory = async (wallet, time) => {
  const network = wallet.coin_network;
  const data = await ApiRequest.get(
    `noneth/${network}/stats/balance/${wallet.address}`,
    {
      params: {
        time,
      },
    }
  );
  return {
    [network]: data,
  };
};

const getStakeTendermintUnbondings = async (address, project) => {
  if (!project.staking.chainId) {
    return {
      [project.name]: 0,
    };
  }
  const result = await StakeApiRequest.get(
    `/delegators/unbonding-delegations/list/${project.name}/${project.staking.chainId}/${address}`
  );
  const total = result
    .map(item =>
      new BN(item.entries[0].balance).dividedBy(project.staking.conversionRate)
    )
    .reduce((sum, value) => new BN(sum).plus(value).toNumber(), 0);
  return {
    [project.name]: total,
  };
};

const timeObjectToArray = (obj, mapFn) => {
  if (!mapFn) mapFn = (obj, key) => obj[key];
  const times = Object.keys(obj).sort((a, b) => a - b);
  return times.map(time => [Number(time), mapFn(obj, time)]);
};
const timeArrayToObject = arr => {
  return arr.reduce((acc, cur) => ({ ...acc, [cur[0]]: cur[1] }), {});
};
const combineTimeData = (sources = []) => {
  const times = [
    ...new Set(sources.reduce((acc, cur) => acc.concat(Object.keys(cur)), [])),
  ];
  return times.reduce((acc, cur) => {
    let obj = {};
    if (
      sources.every(
        item => !Object.keys(item).length || item[cur] !== undefined
      )
    ) {
      obj[cur] = BN.sum(...sources.map(item => item[cur] || 0)).toNumber();
    }
    return {
      ...acc,
      ...obj,
    };
  }, {});
};
//#endregion

const mutations = {
  [mutationTypes.SET_VAULTS_AUM](state, data) {
    state.vaults = data;
  },
  [mutationTypes.SET_ETHEREUM_AUM](state, data) {
    state.ethereums = data;
  },
  [mutationTypes.SET_TENDERMINT_AUM](state, data) {
    state.tendermints = data;
  },
  [mutationTypes.SET_SOLANA_AUM](state, data) {
    state.solanas = data;
  },
  [mutationTypes.SET_SUI_AUM](state, data) {
    state.suis = data;
  },
  [mutationTypes.SET_COINS_AVAILABLE](state, data) {
    state.coinsAvailable = data;
  },
  [mutationTypes.SET_UNBONDINGS](state, data) {
    state.unbondings = data;
  },
  [mutationTypes.SET_HISTORICAL_BALANCES](state, data) {
    state.historicalBalances = data;
  },
};

const getters = {
  vaultsTotalDeposited: (state, getters, rootState, rootGetters) => {
    const getTokenPriceInUsd = rootGetters["appConfig/getTokenPriceInUsd"];
    let value = 0;
    for (const vault in state.vaults) {
      if (Object.hasOwnProperty.call(state.vaults, vault)) {
        for (const key in state.vaults[vault]) {
          if (Object.hasOwnProperty.call(state.vaults[vault], key)) {
            value = new BN(state.vaults[vault][key].expectedReturn)
              .multipliedBy(getTokenPriceInUsd(state.vaults[vault][key].token))
              .plus(value)
              .toNumber();
          }
        }
      }
    }
    return value;
  },
  stakingTotalStaked: (state, getters, rootState, rootGetters) => {
    const getTokenPriceInUsd = rootGetters["appConfig/getTokenPriceInUsd"];
    let value = 0;
    const combined = { ...state.tendermints, ...state.ethereums };
    for (const key in combined) {
      if (Object.hasOwnProperty.call(combined, key)) {
        const item = combined[key];
        value = new BN(item.staked.balance)
          .multipliedBy(getTokenPriceInUsd(item.staked.token))
          .plus(value)
          .toNumber();
      }
    }
    return value;
  },
  stakingTotalRewards: (state, getters, rootState, rootGetters) => {
    const getTokenPriceInUsd = rootGetters["appConfig/getTokenPriceInUsd"];
    let value = 0;
    const combined = { ...state.tendermints, ...state.ethereums };
    for (const key in combined) {
      if (Object.hasOwnProperty.call(combined, key)) {
        const item = combined[key];
        value = new BN(item.rewards.balance)
          .multipliedBy(getTokenPriceInUsd(item.rewards.token))
          .plus(value)
          .toNumber();
      }
    }
    return value;
  },
  stakingAUM: (state, getters) => {
    return new BN(getters.stakingTotalStaked)
      .plus(getters.stakingTotalRewards)
      .toNumber();
  },
  totalAvailable: (state, getters, rootState, rootGetters) => {
    const getTokenPriceInUsd = rootGetters["appConfig/getTokenPriceInUsd"];
    const coinMapNames = rootGetters["appConfig/coinMapNames"];
    let value = 0;
    for (const key in state.coinsAvailable) {
      if (Object.hasOwnProperty.call(state.coinsAvailable, key)) {
        const item = state.coinsAvailable[key];
        const project = coinMapNames[key].project;
        const locked = state.ethereums[project]?.staked.locked ?? 0;
        value = new BN(item.balance - locked)
          .multipliedBy(getTokenPriceInUsd(item.token))
          .plus(value)
          .toNumber();
      }
    }
    return value;
  },
  totalAUM: (state, getters) => {
    return new BN(getters.vaultsTotalDeposited)
      .plus(getters.stakingAUM)
      .toNumber();
  },
  totalValue: (state, getters) => {
    return new BN(getters.totalAUM).plus(getters.totalAvailable).toNumber();
  },
  vaults: state => state.vaults,
  ethereums: state => state.ethereums,
  tendermints: state => state.tendermints,
  solanas: state => state.solanas,
  suis: state => state.suis,
  coinsAvailable: state => state.coinsAvailable,
  unbondings: state => state.unbondings,
  historicalBalances: state => state.historicalBalances,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
